import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquareText, Send, X } from 'lucide-react';
import { api } from '../lib/api';

const post=body=>({method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const title=value=>String(value??'').replaceAll('_',' ').replace(/\b\w/g,char=>char.toUpperCase());
const inServiceWindow=thread=>Boolean(thread?.last_inbound_at&&Date.now()-Date.parse(thread.last_inbound_at)<24*60*60*1000);

export default function WhatsAppComposerModal({
  customerId='',
  initialPhone='',
  customerName='',
  close,
  onSent,
  source='crm_whatsapp_compose',
}){
  const[templates,setTemplates]=useState([]);
  const[templatePack,setTemplatePack]=useState([]);
  const[preference,setPreference]=useState(null);
  const[threads,setThreads]=useState([]);
  const[loading,setLoading]=useState(true);
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');
  const[id,setId]=useState(customerId||'');
  const[phone,setPhone]=useState(initialPhone||'');
  const[mode,setMode]=useState('template');
  const[text,setText]=useState('');
  const[templateName,setTemplateName]=useState('');
  const[language,setLanguage]=useState('en_GB');
  const[purpose,setPurpose]=useState('support');
  const[parameters,setParameters]=useState('');

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [templateResult,inboxResult,prefResult]=await Promise.all([
        api('/api/whatsapp/templates').catch(()=>({templates:[],templatePack:[]})),
        api('/api/whatsapp/inbox').catch(()=>({threads:[]})),
        id.trim()?api('/api/whatsapp/preferences/'+encodeURIComponent(id.trim())).catch(()=>({preference:null})):Promise.resolve({preference:null}),
      ]);
      const approved=(templateResult.templates||[]).filter(row=>String(row.status||'').toUpperCase()==='APPROVED'&&String(row.category||'').toUpperCase()!=='AUTHENTICATION');
      setTemplates(approved);
      setTemplatePack(templateResult.templatePack||[]);
      setThreads(inboxResult.threads||[]);
      setPreference(prefResult.preference||null);
      const matchingThread=(inboxResult.threads||[]).find(row=>
        (id.trim()&&String(row.customer_id||'')===id.trim())||
        (phone&&row.phone_e164===phone)||
        (initialPhone&&row.phone_e164===initialPhone)
      );
      const resolvedPhone=phone||initialPhone||prefResult.preference?.phone_e164||matchingThread?.phone_e164||'';
      if(!phone&&resolvedPhone)setPhone(resolvedPhone);
      const open=inServiceWindow(matchingThread);
      setMode(current=>open&&current==='template'?'text':open?current:'template');
      if(!templateName&&approved.length){
        setTemplateName(approved[0].name);
        setLanguage(approved[0].language||'en_GB');
      }
    }catch(error){
      setNotice('Error: '+(error.message||'WhatsApp compose controls could not load.'));
    }finally{
      setLoading(false);
    }
  },[id,initialPhone,phone,templateName]);

  useEffect(()=>{load();},[]);

  const thread=useMemo(()=>threads.find(row=>
    (id.trim()&&String(row.customer_id||'')===id.trim())||
    (phone&&row.phone_e164===phone)
  )||null,[threads,id,phone]);
  const serviceWindowOpen=inServiceWindow(thread);
  const selectedTemplate=templates.find(row=>row.name===templateName)||null;
  const selectedTemplateDefinition=templatePack.find(row=>row.name===templateName)||null;
  const expectedParameters=selectedTemplateDefinition?.parameterLabels||Array.from({length:Number(selectedTemplate?.parameterCount||0)},(_,index)=>'Parameter '+(index+1));
  const preferenceMatchesPhone=Boolean(preference?.phone_e164&&phone&&preference.phone_e164===phone);
  const serviceOptIn=preferenceMatchesPhone&&Number(preference?.service_opt_in||0)===1;
  const marketingOptIn=preferenceMatchesPhone&&Number(preference?.marketing_opt_in||0)===1;

  useEffect(()=>{
    if(!serviceWindowOpen&&mode==='text')setMode('template');
  },[serviceWindowOpen,mode]);

  const refreshCustomer=async()=>{
    setBusy(true);setNotice('');
    try{await load();}
    finally{setBusy(false);}
  };

  const send=async event=>{
    event.preventDefault();
    if(!phone.trim()){setNotice('Error: Add the customer WhatsApp/mobile number first.');return;}
    if(mode==='text'&&!text.trim()){setNotice('Error: Enter a WhatsApp message.');return;}
    if(mode==='template'&&!templateName){setNotice('Error: Select an approved WhatsApp template.');return;}
    setBusy(true);setNotice('');
    try{
      const lines=parameters.split('\n').map(value=>value.trim()).filter(Boolean);
      if(mode==='template'&&expectedParameters.length!==lines.length)throw new Error('This template requires '+expectedParameters.length+' parameter value'+(expectedParameters.length===1?'':'s')+'.');
      const payload={
        customerId:id.trim()||undefined,
        phone:phone.trim(),
        type:mode,
        purpose,
        source,
        ...(mode==='text'?{text:text.trim()}:{
          templateName,
          language:selectedTemplate?.language||language||'en_GB',
          components:lines.length?[{type:'body',parameters:lines.map(value=>({type:'text',text:value}))}]:[],
        }),
      };
      const result=await api('/api/whatsapp/messages',post(payload));
      if(id.trim()){
        await api('/api/whatsapp/conversations/link',post({customerId:id.trim(),phone:phone.trim()})).catch(()=>undefined);
      }
      setNotice('WhatsApp message sent.');
      onSent?.(result,phone.trim());
    }catch(error){
      setNotice('Error: '+(error.message||'WhatsApp message could not be sent.'));
    }finally{
      setBusy(false);
    }
  };

  const enteredParameters=parameters.split('\n').map(value=>value.trim()).filter(Boolean);
  const parametersReady=mode!=='template'||expectedParameters.length===enteredParameters.length;
  const canSend=!loading&&!busy&&phone.trim()&&(
    mode==='text'
      ?serviceWindowOpen&&text.trim()
      :Boolean(templateName)&&parametersReady
  );

  return <div className="modal-backdrop whatsapp-compose-backdrop" onMouseDown={event=>event.target===event.currentTarget&&!busy&&close?.()}>
    <form className="whatsapp-compose-dialog" onSubmit={send}>
      <header>
        <div className="whatsapp-compose-title"><span><MessageSquareText size={20}/></span><div><small>Customer Operations</small><h2 className="govuk-heading-m">New WhatsApp message</h2>{customerName&&<p className="govuk-body">{customerName}</p>}</div></div>
        <button type="button" className="icon-button" onClick={close} disabled={busy} aria-label="Close WhatsApp composer"><X size={18}/></button>
      </header>

      {notice&&<div className={'operation-notice '+(notice.startsWith('Error:')?'error':'success')}>{notice}</div>}

      <div className="whatsapp-compose-body">
        <div className="whatsapp-compose-grid">
          <label className="govuk-label"><span>Customer ID</span><input className="govuk-input" value={id} onChange={event=>setId(event.target.value)} placeholder="Customer ID / record ID" disabled={Boolean(customerId)}/></label>
          <label className="govuk-label"><span>WhatsApp / mobile number</span><input className="govuk-input" value={phone} onChange={event=>setPhone(event.target.value)} placeholder="+44…" /></label>
        </div>

        <div className="whatsapp-compose-state">
          <div><span>24-hour service window</span><strong>{serviceWindowOpen?'Open':'Closed'}</strong></div>
          <div><span>Service consent</span><strong>{serviceOptIn?'Recorded':'Not recorded'}</strong></div>
          <div><span>Marketing consent</span><strong>{marketingOptIn?'Recorded':'Not recorded'}</strong></div>
        </div>

        <div className="whatsapp-compose-mode">
          <button type="button" className={mode==='text'?'active':''} onClick={()=>serviceWindowOpen&&setMode('text')} disabled={!serviceWindowOpen}>Free-form reply</button>
          <button type="button" className={mode==='template'?'active':''} onClick={()=>setMode('template')}>Approved template</button>
        </div>

        {mode==='text'?<label className="whatsapp-compose-field govuk-label"><span>Message</span><textarea className="govuk-textarea" rows="7" maxLength={4096} value={text} onChange={event=>setText(event.target.value)} placeholder="Write the WhatsApp message…"/><small>Free-form messages are only available during Meta's active 24-hour customer-service window.</small></label>:<>
          <div className="whatsapp-compose-grid">
            <label className="govuk-label"><span>Approved template</span><select className="govuk-select" value={templateName} onChange={event=>{const chosen=templates.find(row=>row.name===event.target.value);setTemplateName(event.target.value);setLanguage(chosen?.language||'en_GB');setParameters('');}}><option value="">Select template</option>{templates.map(row=>{const definition=templatePack.find(item=>item.name===row.name);return <option key={row.id||row.name+'-'+row.language} value={row.name}>{definition?.label||title(row.name)} · {row.language}</option>;})}</select></label>
            <label className="govuk-label"><span>Purpose</span><select className="govuk-select" value={purpose} onChange={event=>setPurpose(event.target.value)}>{['support','service','billing','security','marketing'].map(value=><option key={value} value={value}>{title(value)}</option>)}</select></label>
          </div>
          {selectedTemplateDefinition&&<div className="whatsapp-compose-template-preview"><strong>{selectedTemplateDefinition.label}</strong><p className="govuk-body">{selectedTemplateDefinition.body}</p>{expectedParameters.length>0&&<small>Parameters: {expectedParameters.map((item,index)=>(index+1)+'. '+item).join(' · ')}</small>}</div>}
          <label className="whatsapp-compose-field govuk-label"><span>Template parameters{expectedParameters.length?' · '+expectedParameters.length+' required':''}</span><textarea className="govuk-textarea" rows="5" value={parameters} onChange={event=>setParameters(event.target.value)} placeholder={expectedParameters.length?expectedParameters.map((item,index)=>(index+1)+'. '+item).join('\n'):'No parameters required'}/><small>Enter one value per line in the same order as the template placeholders.</small></label>
          {!templates.length&&!loading&&<div className="whatsapp-compose-warning">There are no approved non-authentication templates available. Create or approve a Meta template before starting a new conversation outside the 24-hour service window.</div>}
        </>}

        {!serviceWindowOpen&&!serviceOptIn&&<div className="whatsapp-compose-warning">This customer does not currently have recorded WhatsApp service consent. Customer Operations will not bypass the existing consent rule for a proactive service message.</div>}
      </div>

      <footer>
        <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={refreshCustomer} disabled={busy}>Refresh status</button>
        <span/>
        <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={close} disabled={busy}>Cancel</button>
        <button type="submit" className="button primary govuk-button" disabled={!canSend}><Send size={15}/>{busy?'Sending…':'Send WhatsApp'}</button>
      </footer>
    </form>
  </div>;
}
